import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/company";
import { canonicalUrl, normalizeOrigin, pageMeta } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/guide", label: "사용 가이드" },
  { to: "/faq", label: "자주 묻는 질문" },
];

const SITE_ORIGIN = normalizeOrigin(import.meta.env.VITE_SITE_ORIGIN);

/**
 * Shell for the public marketing/docs pages: sticky translucent header with
 * the brand lockup + section nav + login CTA, and a shared footer.
 */
export function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  // Signed-in operators get a direct console link instead of the login CTA.
  const isAuthed = useAuthStore((s) => Boolean(s.token));
  const consoleTo = isAuthed ? "/admin" : "/admin/login";

  // canonical/og:url 은 이 레이아웃(=색인 허용 라우트)에서만 심는다. index.html 에 박으면
  // /g/<token>·/p/<token> 에도 같이 붙어 토큰 URL 이 색인 대상으로 선언되고, og:url 때문에
  // 공유 카드가 랜딩으로 연결돼 게스트가 자기 안내 페이지에 못 간다.
  useEffect(() => {
    const href = canonicalUrl(SITE_ORIGIN, location.pathname);
    if (!href) return;
    const link = document.createElement("link");
    link.rel = "canonical";
    link.href = href;
    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = href;
    document.head.append(link, ogUrl);
    return () => {
      link.remove();
      ogUrl.remove();
    };
  }, [location.pathname]);

  // 제목·설명도 여기서 갈아 끼운다. index.html 의 값은 모든 라우트에 고정이라
  // /guide·/faq·/terms·/privacy 가 검색 결과에서 전부 같은 제목으로 뜬다.
  // canonical 과 같은 조건(색인 허용 경로)에서만 바꾸므로 토큰 URL 은 손대지 않는다.
  useEffect(() => {
    const meta = pageMeta(location.pathname);
    if (!meta) return;
    const previousTitle = document.title;
    document.title = meta.title;
    // description·og:title·og:description 은 index.html 이 이미 갖고 있으니
    // 새로 만들지 않고 값만 바꿨다가 되돌린다 — 만들어 붙이면 언마운트 뒤에
    // 원래 태그가 사라진 채 남는다.
    const targets = [
      document.querySelector('meta[name="description"]'),
      document.querySelector('meta[property="og:description"]'),
    ].filter((el): el is HTMLMetaElement => el instanceof HTMLMetaElement);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const previous = targets.map((el) => el.content);
    const previousOgTitle = ogTitle instanceof HTMLMetaElement ? ogTitle.content : null;
    for (const el of targets) el.content = meta.description;
    if (ogTitle instanceof HTMLMetaElement) ogTitle.content = meta.title;
    return () => {
      document.title = previousTitle;
      targets.forEach((el, i) => {
        el.content = previous[i];
      });
      if (ogTitle instanceof HTMLMetaElement && previousOgTitle !== null) {
        ogTitle.content = previousOgTitle;
      }
    };
  }, [location.pathname]);

  return (
    <div className="bg-atmosphere grain flex min-h-dvh flex-col">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label="Stayline 홈" onClick={() => setMenuOpen(false)}>
            <Logo size={34} withWordmark />
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="주요 메뉴">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-full px-4 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Button asChild size="sm" className="ml-2">
              <Link to={consoleTo}>{isAuthed ? "콘솔로 이동" : "로그인"}</Link>
            </Button>
          </nav>

          <button
            type="button"
            className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground hover:bg-secondary/60 md:hidden"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "메뉴 닫기" : "메뉴 열기"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen && (
          <nav
            className="animate-rise border-t border-border/60 bg-background/95 px-4 pb-4 pt-2 md:hidden"
            aria-label="모바일 메뉴"
          >
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "block rounded-lg px-3 py-2.5 text-sm font-medium",
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Button asChild className="mt-2 w-full">
              <Link to={consoleTo} onClick={() => setMenuOpen(false)}>
                {isAuthed ? "콘솔로 이동" : "로그인"}
              </Link>
            </Button>
          </nav>
        )}
      </header>

      <main key={location.pathname} className="relative z-10 flex-1">
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-border/60 bg-background/70">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <Logo size={30} withWordmark />
            <p className="text-xs text-muted-foreground">
              숙소를 위한 AI 게스트 컨시어지 · PMS · 채널 연동
            </p>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label="푸터 메뉴">
            <Link to="/guide" className="text-muted-foreground hover:text-foreground">
              사용 가이드
            </Link>
            <Link to="/faq" className="text-muted-foreground hover:text-foreground">
              자주 묻는 질문
            </Link>
            <Link to={consoleTo} className="text-muted-foreground hover:text-foreground">
              {isAuthed ? "콘솔로 이동" : "운영자 로그인"}
            </Link>
          </nav>
        </div>
        <div className="border-t border-border/40">
          <div className="mx-auto w-full max-w-6xl space-y-3 px-4 py-6 sm:px-6">
            <nav className="flex flex-wrap gap-x-4 gap-y-1 text-xs" aria-label="약관 및 정책">
              <Link to="/terms" className="text-muted-foreground hover:text-foreground">
                이용약관
              </Link>
              {/* 개인정보처리방침은 관례에 따라 강조 표기 */}
              <Link
                to="/privacy"
                className="font-semibold text-foreground/80 hover:text-foreground"
              >
                개인정보 처리방침
              </Link>
            </nav>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {COMPANY.name} · 대표 {COMPANY.ceo} · 사업자등록번호 {COMPANY.bizRegNo} ·
              통신판매업신고번호 {COMPANY.ecommerceRegNo}
              <br />
              {COMPANY.address} · 이메일 {COMPANY.email} · 전화 {COMPANY.phone}
            </p>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {COMPANY.name} All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
